import { useEffect, useRef, useState } from 'react';
import { cn } from './ui';

export interface MehrEintrag {
  label: string;
  onSelect: () => void;
  /** Rot und abgesetzt: Loeschen und aehnliches. */
  gefaehrlich?: boolean;
}

/**
 * "⋯"-Menue fuer seltene Aktionen einer Zeile.
 *
 * Die Hauptaktion bleibt sichtbar; Absagen, Loeschen und Co. liegen hier, damit
 * sie nicht gleichrangig daneben stehen und am Handy keine eigene Zeile brauchen.
 */
export function MehrMenu({ label, eintraege }: { label: string; eintraege: MehrEintrag[] }): JSX.Element | null {
  const [offen, setOffen] = useState(false);
  const huelle = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!offen) return undefined;
    const schliessen = (event: MouseEvent | KeyboardEvent): void => {
      if (event instanceof KeyboardEvent ? event.key === 'Escape' : !huelle.current?.contains(event.target as Node)) setOffen(false);
    };
    document.addEventListener('mousedown', schliessen);
    document.addEventListener('keydown', schliessen);
    return () => {
      document.removeEventListener('mousedown', schliessen);
      document.removeEventListener('keydown', schliessen);
    };
  }, [offen]);

  if (eintraege.length === 0) return null;
  return (
    <div ref={huelle} className="relative">
      <button
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={offen}
        onClick={() => setOffen(!offen)}
        className="flex h-9 w-9 items-center justify-center rounded-lg text-lg font-semibold text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {offen && (
        <div role="menu" className="absolute right-0 z-20 mt-1 min-w-44 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
          {eintraege.map((eintrag, index) => (
            <button
              key={eintrag.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setOffen(false);
                eintrag.onSelect();
              }}
              className={cn(
                'block w-full px-3 py-2 text-left text-sm transition-colors',
                eintrag.gefaehrlich ? 'text-rose-700 hover:bg-rose-50' : 'text-slate-700 hover:bg-slate-50',
                eintrag.gefaehrlich && index > 0 && 'border-t border-slate-100',
              )}
            >
              {eintrag.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
